'use client';

import { useUser } from '@/context/UserContext';
import { useState, useEffect, useCallback } from 'react';

const ONBOARDING_KEY = 'hasCompletedBrandOnboarding';

/**
 * Hook to check if the brand onboarding modal should be shown
 * Shows modal for new users who haven't configured their brand yet
 */
export function useOnboardingCheck() {
  const { user, isLoading } = useUser();
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Wait for user data to load
    if (isLoading || !user) {
      return;
    }

    // Only check once per mount
    if (hasChecked) {
      return;
    }

    // Check if user has already completed or dismissed onboarding
    const storageKey = `${ONBOARDING_KEY}_${user.id}`;
    const hasCompleted = localStorage.getItem(storageKey);

    if (hasCompleted) {
      setShouldShowOnboarding(false);
      setHasChecked(true);
      return;
    }

    // Check if user is "new" - no logo and no primary color configured
    const profile = user.profile;
    const isNewUser = !profile?.logoUrl && !profile?.primaryColor;

    // Show onboarding only for new users who haven't dismissed it
    setShouldShowOnboarding(isNewUser);
    setHasChecked(true);
  }, [user, isLoading, hasChecked]);

  /**
   * Mark onboarding as completed (user configured their brand)
   */
  const completeOnboarding = useCallback(() => {
    if (user) {
      const storageKey = `${ONBOARDING_KEY}_${user.id}`;
      localStorage.setItem(storageKey, 'completed');
      setShouldShowOnboarding(false);
    }
  }, [user]);

  /**
   * Dismiss onboarding without completing (user clicked "Não, depois")
   */
  const dismissOnboarding = useCallback(() => {
    if (user) {
      const storageKey = `${ONBOARDING_KEY}_${user.id}`;
      localStorage.setItem(storageKey, 'dismissed');
      setShouldShowOnboarding(false);
    }
  }, [user]);

  return {
    shouldShowOnboarding,
    isCheckingOnboarding: isLoading || !hasChecked,
    completeOnboarding,
    dismissOnboarding
  };
}
